import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useWidgetStore, type ChartCard as ChartCardData } from '@/state/widgetStore';
import { buildChartSpec } from './chartBuilder';
import { ApexChart } from './ApexChart';

const INITIAL_WIDTH = 600;
const INITIAL_HEIGHT = 460;
const MIN_WIDTH = 360;
const MIN_HEIGHT = 280;

export function ChartCard({ card }: { card: ChartCardData }) {
  const removeChart = useWidgetStore((s) => s.removeChart);
  const chartBoxRef = useRef<HTMLDivElement | null>(null);
  const [chartSize, setChartSize] = useState({
    width: INITIAL_WIDTH - 24,
    height: INITIAL_HEIGHT - 40 - 24,
  });

  useEffect(() => {
    const el = chartBoxRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setChartSize({
        width: Math.max(0, Math.round(rect.width)),
        height: Math.max(0, Math.round(rect.height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const spec = useMemo(() => {
    try {
      return buildChartSpec(card);
    } catch (err) {
      return { error: (err as Error).message };
    }
  }, [card]);

  return (
    <div
      className="pointer-events-auto flex resize flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-xl"
      style={{
        width: INITIAL_WIDTH,
        height: INITIAL_HEIGHT,
        minWidth: MIN_WIDTH,
        minHeight: MIN_HEIGHT,
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 8rem)',
      }}
    >
      <div className="flex h-10 shrink-0 items-center justify-between border-b border-border px-4">
        <div className="truncate text-sm font-semibold">{card.title}</div>
        <button
          type="button"
          onClick={() => removeChart(card.id)}
          className="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          aria-label="Close chart"
        >
          <X className="size-4" />
        </button>
      </div>
      <div ref={chartBoxRef} className="min-h-0 flex-1 px-3 py-3">
        {'error' in spec ? (
          <div className="p-3 text-xs text-destructive">{spec.error}</div>
        ) : chartSize.width > 0 && chartSize.height > 0 ? (
          <ApexChart
            options={spec.options}
            series={spec.series}
            type={spec.type}
            width={chartSize.width}
            height={chartSize.height}
          />
        ) : null}
      </div>
    </div>
  );
}
